import classnames from 'classnames';
import React, {useRef} from 'react';

import {Icon, IconButton, Text} from '@albert-io/atomic';
import useAutoFocusReturn from 'lib/hooks/useAutoFocusReturn';
import usePrevious from 'lib/hooks/usePrevious';

import {useAnnotationModeStore} from './AnnotationModeStore';
import type {QuestionNote} from './annotationNotes';
import AnnotationNotesPanelList from './AnnotationNotesPanelList';
import {useIsAnnotationModeAvailable} from './useIsAnnotationModeAvailable';

const PANEL_TITLE_ID = 'annotation-notes-panel-title';

/** The Figma's width (2692:5224 placed in 3478:24413); panel widths have no tokens. */
const PANEL_WIDTH_CLASS = 'w-[343px]';

interface AnnotationNotesPanelProps {
  /** The question whose notes are listed; undefined while one loads. */
  questionId: string | undefined;
  /** Opens a note for editing (#20497). Without it the pencil is inert. */
  onEditNote?: (note: QuestionNote) => void;
}

/**
 * The question's notes, docked beside it rather than over it. Stays mounted
 * while closed so the width can animate; `inert` keeps its controls untabbable.
 */
export default function AnnotationNotesPanel({questionId, onEditNote}: AnnotationNotesPanelProps) {
  const isAnnotationModeAvailable = useIsAnnotationModeAvailable();
  const isOpen = useAnnotationModeStore((state) => state.isNotesPanelOpen);
  const closeNotesPanel = useAnnotationModeStore((state) => state.closeNotesPanel);
  const panelRef = useRef<HTMLElement>(null);
  const wasOpen = usePrevious(isOpen);
  // Never true on a remount: PracticeView remounts per question navigation, and
  // an already-open panel must not steal focus from the question.
  const justOpened = isOpen && wasOpen === false;
  const restoreFocus = useAutoFocusReturn(panelRef, justOpened);

  // Focus must leave before `inert` pins it to a disabled control. Only keyboard
  // closes then call `restoreFocus`: the indicator's peek popover opens on focus,
  // so handing it back after a mouse close would spring the peek open again.
  const close = () => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    closeNotesPanel();
  };

  if (!isAnnotationModeAvailable) {
    return null;
  }

  return (
    <aside
      ref={panelRef}
      aria-labelledby={PANEL_TITLE_ID}
      inert={!isOpen}
      data-testid='annotation-notes-panel'
      className={classnames(
        'shrink-0 overflow-hidden transition-[width] duration-250 ease-linear motion-reduce:transition-none',
        isOpen ? PANEL_WIDTH_CLASS : 'w-x0'
      )}
      onKeyDown={(event) => {
        // preventDefault makes the document-level mode-exit Escape listener yield.
        if (event.key === 'Escape') {
          event.preventDefault();
          close();
          restoreFocus();
        }
      }}
    >
      {/* Fixed width: clipped rather than reflowed while the outer box animates. */}
      <div
        className={classnames(
          // No border-solid: without preflight it paints 3px UA borders on unsized sides.
          'background-subtle border-border-primary flex h-full flex-col border-l',
          PANEL_WIDTH_CLASS
        )}
      >
        <header className='gap-x1 px-x2 py-x1-half background-primary border-border-primary flex shrink-0 items-center justify-between border-b'>
          {/* Icon inside the title so it inherits the 15px size; font-normal and
              m-x0 shed the h2's UA bold and legacy margin. */}
          <Text
            as='h2'
            id={PANEL_TITLE_ID}
            size='m'
            color='primary'
            className='gap-x1 m-x0 flex items-center font-normal'
          >
            <Icon icon='notes' iconStyle='regular' />
            Notes
          </Text>
          <IconButton
            icon='times'
            iconStyle='regular'
            label='Close notes'
            size='s'
            onClick={(event: React.MouseEvent) => {
              close();
              // detail 0 marks a keyboard-activated click (Enter/Space).
              if (event.detail === 0) {
                restoreFocus();
              }
            }}
          />
        </header>
        {/* min-h-0: without it this flex child refuses to shrink and never scrolls. */}
        <div className='min-h-0 grow overflow-y-auto'>
          <AnnotationNotesPanelList questionId={questionId} onEditNote={onEditNote} />
        </div>
      </div>
    </aside>
  );
}
